import { useState, useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { T } from '@design/index';
import { patientApi } from '../../api';

// ─── Inline SVG Icons ─────────────────────────────────────────────────────────

function IconFlask({ color, size = 22 }: { color: string; size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M9 3h6" /><path d="M10 3v6l-4 9h12l-4-9V3" />
      <circle cx="9.5" cy="15.5" r="0.8" fill={color} stroke="none" />
      <circle cx="12.5" cy="17" r="0.6" fill={color} stroke="none" />
    </svg>
  );
}

function IconShield({ color, size = 12 }: { color: string; size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 2l8 4v6c0 5-3.5 9.7-8 11-4.5-1.3-8-6-8-11V6l8-4z" />
      <path d="M9 12l2 2 4-4" />
    </svg>
  );
}

function IconMapPin({ color }: { color: string }) {
  return (
    <svg width={13} height={13} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
      <circle cx="12" cy="10" r="3" />
    </svg>
  );
}

function IconPlus({ color }: { color: string }) {
  return (
    <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 5v14" /><path d="M5 12h14" />
    </svg>
  );
}

function IconCheck({ color }: { color: string }) {
  return (
    <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M20 6 9 17l-5-5" />
    </svg>
  );
}

function IconArrowRight({ color }: { color: string }) {
  return (
    <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M5 12h14" /><path d="M12 5l7 7-7 7" />
    </svg>
  );
}

function IconClock({ color }: { color: string }) {
  return (
    <svg width={11} height={11} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10" />
      <path d="M12 6v6l4 2" />
    </svg>
  );
}

function IconHome({ color }: { color: string }) {
  return (
    <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
      <polyline points="9 22 9 12 15 12 15 22" />
    </svg>
  );
}

function IconInfo({ color }: { color: string }) {
  return (
    <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10" />
      <path d="M12 16v-4" /><path d="M12 8h.01" />
    </svg>
  );
}

function IconPackage({ color, size = 22 }: { color: string; size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="21 8 21 21 3 21 3 8" />
      <rect x="1" y="3" width="22" height="5" />
      <line x1="10" y1="12" x2="14" y2="12" />
    </svg>
  );
}

function IconSearch({ color }: { color: string }) {
  return (
    <svg width={15} height={15} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="11" cy="11" r="8" />
      <path d="M21 21l-4.35-4.35" />
    </svg>
  );
}

function IconX({ color }: { color: string }) {
  return (
    <svg width={12} height={12} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M18 6 6 18" /><path d="m6 6 12 12" />
    </svg>
  );
}

function IconTag({ color }: { color: string }) {
  return (
    <svg width={11} height={11} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z" />
      <line x1="7" y1="7" x2="7.01" y2="7" />
    </svg>
  );
}

// ─── Types ────────────────────────────────────────────────────────────────────

interface LabProfile {
  labProfileId?: string;
  id?: string;
  labName?: string;
  name?: string;
  address?: string;
  city?: string;
  accreditation?: string;
  verified?: boolean;
  homeCollectionAvailable?: boolean;
  operatingHours?: string;
  phone?: string;
  email?: string;
}

interface LabTest {
  id: string;
  name: string;
  price: number;
  category?: string;
  specimen?: string;
  tat?: string;
  fasting?: boolean;
  homeCollectionAvailable?: boolean;
}

interface LabPackage {
  id: string;
  name: string;
  price: number;
  discountedPrice?: number;
  tests?: string[] | { name: string }[];
  testCount?: number;
}

interface CartItem {
  id: string;
  name: string;
  price: number;
  type: 'test' | 'package';
}

type TabType = 'tests' | 'packages' | 'info';

// ─── Shimmer Skeleton ─────────────────────────────────────────────────────────

function Shimmer({ width, height, radius = 8 }: { width?: string | number; height: number; radius?: number }) {
  return (
    <div
      style={{
        width: width ?? '100%',
        height,
        borderRadius: radius,
        background: T.border,
        animation: 'pulse 1.4s ease infinite',
      }}
    />
  );
}

// ─── Test Row ─────────────────────────────────────────────────────────────────

function TestRow({ test, inCart, onAdd, onCompare }: { test: LabTest; inCart: boolean; onAdd: () => void; onCompare: () => void }) {
  return (
    <div
      style={{
        background: T.white,
        border: `1px solid ${inCart ? T.teal + '40' : T.border}`,
        borderRadius: 14,
        padding: '14px 14px',
        display: 'flex',
        alignItems: 'flex-start',
        gap: 12,
        transition: 'border-color 0.12s ease',
      }}
    >
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontFamily: T.font, fontSize: 13, fontWeight: 700, color: T.text, lineHeight: 1.3, marginBottom: 5 }}>
          {test.name}
        </div>

        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
          {test.category && (
            <span style={{ fontFamily: T.font, fontSize: 10, color: T.textTer, background: T.bg, borderRadius: 6, padding: '2px 7px', border: `1px solid ${T.border}` }}>
              {test.category}
            </span>
          )}
          {test.specimen && (
            <span style={{ display: 'flex', alignItems: 'center', gap: 3, fontFamily: T.font, fontSize: 10, color: T.textSec }}>
              <IconTag color={T.textTer} />
              {test.specimen}
            </span>
          )}
          {test.tat && (
            <span style={{ display: 'flex', alignItems: 'center', gap: 3, fontFamily: T.font, fontSize: 10, color: T.textSec }}>
              <IconClock color={T.textTer} />
              {test.tat}
            </span>
          )}
          {test.fasting && (
            <span style={{ fontFamily: T.font, fontSize: 9, fontWeight: 700, color: '#D97706', background: '#FEF9C3', border: '1px solid #FDE68A', borderRadius: 5, padding: '1px 6px' }}>
              Fasting
            </span>
          )}
          {test.homeCollectionAvailable && (
            <span style={{ display: 'flex', alignItems: 'center', gap: 3, fontFamily: T.font, fontSize: 9, fontWeight: 600, color: T.teal, background: T.teal + '12', border: `1px solid ${T.teal}28`, borderRadius: 5, padding: '1px 6px' }}>
              <IconHome color={T.teal} /> Home
            </span>
          )}
        </div>
      </div>

      <div style={{ flexShrink: 0, display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 8 }}>
        <span style={{ fontFamily: T.font, fontSize: 14, fontWeight: 800, color: T.navy }}>
          ₹{(test.price || 0).toLocaleString('en-IN')}
        </span>
        <button
          onClick={onAdd}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 4,
            background: inCart ? T.teal + '14' : T.teal,
            border: `1.5px solid ${inCart ? T.teal : T.teal}`,
            borderRadius: 8,
            padding: '5px 11px',
            fontFamily: T.font,
            fontSize: 11,
            fontWeight: 700,
            color: inCart ? T.teal : T.white,
            cursor: 'pointer',
            transition: 'background 0.12s ease',
          }}
        >
          {inCart ? <><IconCheck color={T.teal} /> Added</> : <><IconPlus color={T.white} /> Add</>}
        </button>
        {/* One lab's price means little on its own; this asks every other
            lab on the network what it charges for the same test. */}
        <button
          onClick={onCompare}
          style={{
            background: 'none',
            border: 'none',
            padding: 0,
            fontFamily: T.font,
            fontSize: 10.5,
            fontWeight: 700,
            color: T.textSec,
            cursor: 'pointer',
            textDecoration: 'underline',
          }}
        >
          Compare price
        </button>
      </div>
    </div>
  );
}

// ─── Package Row ──────────────────────────────────────────────────────────────

function PackageRow({ pkg, inCart, onAdd }: { pkg: LabPackage; inCart: boolean; onAdd: () => void }) {
  const displayPrice = pkg.discountedPrice ?? pkg.price ?? 0;
  const hasDiscount = pkg.discountedPrice != null && pkg.price != null && pkg.discountedPrice < pkg.price;
  const savings = hasDiscount ? (pkg.price ?? 0) - (pkg.discountedPrice ?? 0) : 0;
  const testCount = pkg.testCount ?? (Array.isArray(pkg.tests) ? pkg.tests.length : 0);

  return (
    <div
      style={{
        background: T.white,
        border: `1px solid ${inCart ? T.purple + '40' : T.border}`,
        borderRadius: 14,
        padding: '14px 14px',
        display: 'flex',
        alignItems: 'flex-start',
        gap: 12,
        transition: 'border-color 0.12s ease',
      }}
    >
      <div
        style={{
          width: 40,
          height: 40,
          borderRadius: 12,
          background: T.purple + '14',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}
      >
        <IconPackage color={T.purple} size={20} />
      </div>

      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontFamily: T.font, fontSize: 13, fontWeight: 700, color: T.text, lineHeight: 1.3, marginBottom: 5 }}>
          {pkg.name}
        </div>
        {testCount > 0 && (
          <div style={{ fontFamily: T.font, fontSize: 11, color: T.textSec, marginBottom: 4 }}>
            Includes {testCount} test{testCount !== 1 ? 's' : ''}
          </div>
        )}
        {hasDiscount && (
          <div
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 4,
              background: '#059669' + '12',
              border: `1px solid #05966928`,
              borderRadius: 6,
              padding: '2px 7px',
            }}
          >
            <span style={{ fontFamily: T.font, fontSize: 10, fontWeight: 700, color: '#059669' }}>
              Save ₹{savings.toLocaleString('en-IN')}
            </span>
          </div>
        )}
      </div>

      <div style={{ flexShrink: 0, display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 6 }}>
        <div style={{ textAlign: 'right' }}>
          <div style={{ fontFamily: T.font, fontSize: 15, fontWeight: 800, color: T.navy }}>
            ₹{displayPrice.toLocaleString('en-IN')}
          </div>
          {hasDiscount && (
            <div style={{ fontFamily: T.font, fontSize: 10, color: T.textTer, textDecoration: 'line-through' }}>
              ₹{pkg.price.toLocaleString('en-IN')}
            </div>
          )}
        </div>
        <button
          onClick={onAdd}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 4,
            background: inCart ? T.purple + '14' : T.purple,
            border: `1.5px solid ${T.purple}`,
            borderRadius: 8,
            padding: '5px 11px',
            fontFamily: T.font,
            fontSize: 11,
            fontWeight: 700,
            color: inCart ? T.purple : T.white,
            cursor: 'pointer',
            transition: 'background 0.12s ease',
          }}
        >
          {inCart ? <><IconCheck color={T.purple} /> Added</> : <><IconPlus color={T.white} /> Add</>}
        </button>
      </div>
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────

export default function LabStore() {
  const { labId = '' } = useParams<{ labId: string }>();
  const navigate = useNavigate();

  const [activeTab, setActiveTab] = useState<TabType>('tests');
  const [labProfile, setLabProfile] = useState<LabProfile | null>(null);
  const [tests, setTests] = useState<LabTest[]>([]);
  const [packages, setPackages] = useState<LabPackage[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [testSearch, setTestSearch] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);

  // Cart with localStorage persistence
  const [cart, setCart] = useState<CartItem[]>(() => {
    try {
      const saved = localStorage.getItem(`nhc_lab_cart_${labId}`);
      return saved ? JSON.parse(saved) : [];
    } catch { return []; }
  });

  useEffect(() => {
    try {
      localStorage.setItem(`nhc_lab_cart_${labId}`, JSON.stringify(cart));
    } catch { /* ignore */ }
  }, [cart, labId]);

  useEffect(() => {
    let alive = true;
    if (!labId) return;
    const load = async () => {
      setLoading(true);
      setError(null);
      // The lab's own profile is real (backed by the provider directory);
      // a per-lab test/package catalog isn't a feature yet, so those two
      // calls are independent and failing doesn't hide a real profile.
      const profileRes = await patientApi.lab.getLabProfile(labId).catch(() => null);
      if (alive) {
        if (profileRes) setLabProfile((profileRes as any)?.data ?? profileRes);
        else setError('Unable to load lab details.');
      }

      const testsRes = await patientApi.lab.getLabTests(labId).catch(() => null);
      if (alive) {
        const testsData = (testsRes as any)?.data ?? testsRes ?? [];
        const testList = Array.isArray(testsData) ? testsData : testsData.tests ?? [];
        // Tests render exactly as the lab published them — specimen, TAT and
        // fasting requirements are clinical facts and are never guessed here.
        // The catalogue sends `sampleType` and `turnaroundHours`; reading them
        // under the old `specimen`/`tat` names meant both were always blank.
        setTests(testList.map((t: any) => ({
          ...t,
          specimen: t.specimen ?? t.sampleType ?? undefined,
          tat: t.tat ?? (typeof t.turnaroundHours === 'number' && t.turnaroundHours > 0
            ? (t.turnaroundHours < 24
              ? `${t.turnaroundHours} h`
              : `${Math.round(t.turnaroundHours / 24)} day${Math.round(t.turnaroundHours / 24) === 1 ? '' : 's'}`)
            : undefined),
        })));
      }

      const pkgsRes = await patientApi.lab.getLabPackages(labId).catch(() => null);
      if (alive) {
        const pkgsData = (pkgsRes as any)?.data ?? pkgsRes ?? [];
        const pkgList = Array.isArray(pkgsData) ? pkgsData : pkgsData.packages ?? [];
        setPackages(pkgList);
      }

      if (alive) setLoading(false);
    };
    load();
    return () => { alive = false; };
  }, [labId]);

  const toggleCart = (item: CartItem) => {
    setCart((prev) => {
      const exists = prev.find((c) => c.id === item.id && c.type === item.type);
      if (exists) return prev.filter((c) => !(c.id === item.id && c.type === item.type));
      return [...prev, item];
    });
  };

  const isInCart = (id: string, type: 'test' | 'package') =>
    cart.some((c) => c.id === id && c.type === type);

  const cartTotal = cart.reduce((sum, c) => sum + (c.price || 0), 0);
  const cartCount = cart.length;

  // Derive categories from test data
  const testCategories = useMemo(() => {
    const cats = new Set<string>();
    tests.forEach((t) => { if (t.category) cats.add(t.category); });
    return Array.from(cats).sort();
  }, [tests]);

  // Filtered tests based on search + category
  const filteredTests = useMemo(() => {
    let list = tests;
    if (testSearch.trim()) {
      const q = testSearch.trim().toLowerCase();
      list = list.filter((t) => t.name.toLowerCase().includes(q) || (t.category && t.category.toLowerCase().includes(q)));
    }
    if (selectedCategory) {
      list = list.filter((t) => t.category === selectedCategory);
    }
    return list;
  }, [tests, testSearch, selectedCategory]);

  // Filtered packages based on search
  const filteredPackages = useMemo(() => {
    if (!testSearch.trim()) return packages;
    const q = testSearch.trim().toLowerCase();
    return packages.filter((p) => p.name.toLowerCase().includes(q));
  }, [packages, testSearch]);

  const handleBookNow = () => {
    const testIds = cart.filter((c) => c.type === 'test').map((c) => c.id).join(',');
    const packageIds = cart.filter((c) => c.type === 'package').map((c) => c.id).join(',');
    const params = new URLSearchParams();
    if (testIds) params.set('tests', testIds);
    if (packageIds) params.set('packages', packageIds);
    navigate(`/patient/care-services/labs/${labId}/book?${params.toString()}`);
  };

  const labName = labProfile?.labName || labProfile?.name || 'Lab';
  const TABS: { key: TabType; label: string }[] = [
    { key: 'tests', label: 'Tests' },
    { key: 'packages', label: 'Packages' },
    { key: 'info', label: 'Info' },
  ];

  return (
    <div
      style={{
        background: T.bg,
        fontFamily: T.font,
        maxWidth: 1080,
        margin: '0 auto',
        paddingBottom: cartCount > 0 ? 140 : 100,
      }}
    >
      <style>{`
        @keyframes pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.45; } }
        @keyframes slideUp { from { transform: translateY(100%); opacity: 0; } to { transform: translateY(0); opacity: 1; } }
      `}</style>

      {/* ── Lab Hero ─────────────────────────────────────────────── */}
      <div
        style={{
          background: `linear-gradient(145deg, ${T.teal} 0%, #007A7A 60%, ${T.navy} 100%)`,
          padding: '40px 20px 28px',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <div style={{ position: 'absolute', top: -25, right: -25, width: 130, height: 130, borderRadius: '50%', background: 'rgba(255,255,255,0.05)' }} />
        <div style={{ position: 'absolute', bottom: 0, left: -15, width: 90, height: 90, borderRadius: '50%', background: 'rgba(0,40,80,0.12)' }} />

        {loading ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <Shimmer height={26} width="60%" />
            <Shimmer height={16} width="40%" />
          </div>
        ) : (
          <>
            {/* Name + badges */}
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, marginBottom: 10 }}>
              <div style={{ width: 52, height: 52, borderRadius: 16, background: 'rgba(255,255,255,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <IconFlask color="white" size={26} />
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                  <h1 style={{ fontFamily: T.font, fontSize: 20, fontWeight: 800, color: T.white, margin: 0, lineHeight: 1.25 }}>
                    {labName}
                  </h1>
                  {!!labProfile?.accreditation && (
                    <span
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 4,
                        background: 'rgba(255,255,255,0.15)',
                        border: '1px solid rgba(255,255,255,0.3)',
                        borderRadius: 8,
                        padding: '2px 8px',
                        fontFamily: T.font,
                        fontSize: 10,
                        fontWeight: 700,
                        color: T.white,
                        letterSpacing: '0.04em',
                      }}
                    >
                      <IconShield color="white" />
                      {labProfile?.accreditation}
                    </span>
                  )}
                </div>
                {(labProfile?.address || labProfile?.city) && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginTop: 6 }}>
                    <IconMapPin color="rgba(255,255,255,0.7)" />
                    <span style={{ fontFamily: T.font, fontSize: 12, color: 'rgba(255,255,255,0.8)' }}>
                      {[labProfile?.address, labProfile?.city].filter(Boolean).join(', ')}
                    </span>
                  </div>
                )}
                {labProfile?.homeCollectionAvailable && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginTop: 5 }}>
                    <IconHome color="rgba(255,255,255,0.7)" />
                    <span style={{ fontFamily: T.font, fontSize: 11, color: 'rgba(255,255,255,0.8)', fontWeight: 600 }}>
                      Home collection available
                    </span>
                  </div>
                )}
              </div>
            </div>
          </>
        )}
      </div>

      {/* ── Tab Bar ──────────────────────────────────────────────── */}
      <div
        style={{
          background: T.white,
          borderBottom: `1px solid ${T.border}`,
          display: 'flex',
          padding: '0 16px',
          position: 'sticky',
          top: 0,
          zIndex: 20,
        }}
      >
        {TABS.map(({ key, label }) => (
          <button
            key={key}
            onClick={() => setActiveTab(key)}
            style={{
              flex: 1,
              padding: '14px 0 12px',
              fontFamily: T.font,
              fontSize: 13,
              fontWeight: activeTab === key ? 700 : 500,
              color: activeTab === key ? T.teal : T.textSec,
              background: 'none',
              border: 'none',
              borderBottom: `2.5px solid ${activeTab === key ? T.teal : 'transparent'}`,
              cursor: 'pointer',
              transition: 'color 0.12s ease, border-color 0.12s ease',
              marginBottom: -1,
            }}
          >
            {label}
            {key === 'tests' && tests.length > 0 && (
              <span style={{ marginLeft: 5, fontSize: 10, color: activeTab === 'tests' ? T.teal : T.textTer, fontWeight: 600 }}>
                ({tests.length})
              </span>
            )}
            {key === 'packages' && packages.length > 0 && (
              <span style={{ marginLeft: 5, fontSize: 10, color: activeTab === 'packages' ? T.teal : T.textTer, fontWeight: 600 }}>
                ({packages.length})
              </span>
            )}
          </button>
        ))}
      </div>

      {/* ── Tab Content ──────────────────────────────────────────── */}
      <div style={{ padding: '16px 16px 0' }}>
        {error && (
          <div style={{ background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: 12, padding: '14px 16px', marginBottom: 14 }}>
            <p style={{ fontFamily: T.font, fontSize: 13, color: '#DC2626', margin: 0 }}>{error}</p>
          </div>
        )}

        {/* ── Search bar (for tests & packages tabs) ── */}
        {(activeTab === 'tests' || activeTab === 'packages') && !loading && (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              background: T.white,
              borderRadius: 12,
              padding: '10px 14px',
              border: `1px solid ${T.border}`,
              marginBottom: 10,
            }}
          >
            <IconSearch color={T.textTer} />
            <input
              type="text"
              placeholder={activeTab === 'tests' ? 'Search tests...' : 'Search packages...'}
              value={testSearch}
              onChange={(e) => setTestSearch(e.target.value)}
              style={{
                flex: 1,
                border: 'none',
                outline: 'none',
                fontFamily: T.font,
                fontSize: 12,
                color: T.text,
                background: 'transparent',
                padding: 0,
              }}
            />
            {testSearch && (
              <button onClick={() => setTestSearch('')} style={{ background: 'none', border: 'none', padding: 2, cursor: 'pointer', display: 'flex', alignItems: 'center', opacity: 0.5 }}>
                <IconX color={T.text} />
              </button>
            )}
          </div>
        )}

        {/* ── Category chips (tests tab only) ── */}
        {activeTab === 'tests' && !loading && testCategories.length > 0 && (
          <div style={{ display: 'flex', gap: 6, overflowX: 'auto', marginBottom: 10, paddingBottom: 2 }}>
            <button
              onClick={() => setSelectedCategory(null)}
              style={{
                flexShrink: 0,
                background: selectedCategory === null ? T.teal : T.white,
                border: `1.5px solid ${selectedCategory === null ? T.teal : T.border}`,
                borderRadius: 16,
                padding: '4px 12px',
                fontFamily: T.font,
                fontSize: 11,
                fontWeight: 600,
                color: selectedCategory === null ? T.white : T.textSec,
                cursor: 'pointer',
              }}
            >
              All
            </button>
            {testCategories.map((cat) => (
              <button
                key={cat}
                onClick={() => setSelectedCategory(selectedCategory === cat ? null : cat)}
                style={{
                  flexShrink: 0,
                  background: selectedCategory === cat ? T.teal : T.white,
                  border: `1.5px solid ${selectedCategory === cat ? T.teal : T.border}`,
                  borderRadius: 16,
                  padding: '4px 12px',
                  fontFamily: T.font,
                  fontSize: 11,
                  fontWeight: 600,
                  color: selectedCategory === cat ? T.white : T.textSec,
                  cursor: 'pointer',
                }}
              >
                {cat}
              </button>
            ))}
          </div>
        )}

        {/* ── Tests Tab ── */}
        {activeTab === 'tests' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {loading ? (
              Array.from({ length: 5 }).map((_, i) => (
                <div key={i} style={{ background: T.white, borderRadius: 14, padding: 14, display: 'flex', gap: 12 }}>
                  <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 8 }}>
                    <Shimmer height={14} width="70%" />
                    <Shimmer height={11} width="45%" />
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8, alignItems: 'flex-end' }}>
                    <Shimmer height={16} width={60} />
                    <Shimmer height={28} width={60} />
                  </div>
                </div>
              ))
            ) : filteredTests.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '40px 0' }}>
                <p style={{ fontFamily: T.font, fontSize: 13, color: T.textSec, margin: 0 }}>
                  {testSearch || selectedCategory ? 'No tests match your filters.' : 'No tests available for this lab.'}
                </p>
              </div>
            ) : (
              filteredTests.map((test) => (
                <TestRow
                  key={test.id}
                  test={test}
                  inCart={isInCart(test.id, 'test')}
                  onAdd={() => toggleCart({ id: test.id, name: test.name, price: test.price, type: 'test' })}
                  onCompare={() => navigate(`/patient/care-services/labs/compare?test=${encodeURIComponent(test.name)}`)}
                />
              ))
            )}
          </div>
        )}

        {/* ── Packages Tab ── */}
        {activeTab === 'packages' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {loading ? (
              Array.from({ length: 3 }).map((_, i) => (
                <div key={i} style={{ background: T.white, borderRadius: 14, padding: 14, display: 'flex', gap: 12 }}>
                  <Shimmer height={40} width={40} radius={12} />
                  <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 8 }}>
                    <Shimmer height={14} width="65%" />
                    <Shimmer height={11} width="40%" />
                  </div>
                </div>
              ))
            ) : filteredPackages.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '40px 0' }}>
                <p style={{ fontFamily: T.font, fontSize: 13, color: T.textSec, margin: 0 }}>
                  {testSearch ? 'No packages match your search.' : 'No packages available for this lab.'}
                </p>
              </div>
            ) : (
              filteredPackages.map((pkg) => (
                <PackageRow
                  key={pkg.id}
                  pkg={pkg}
                  inCart={isInCart(pkg.id, 'package')}
                  onAdd={() => {
                    const price = pkg.discountedPrice ?? pkg.price;
                    toggleCart({ id: pkg.id, name: pkg.name, price, type: 'package' });
                  }}
                />
              ))
            )}
          </div>
        )}

        {/* ── Info Tab ── */}
        {activeTab === 'info' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {loading ? (
              Array.from({ length: 4 }).map((_, i) => (
                <div key={i} style={{ background: T.white, borderRadius: 14, padding: 16, display: 'flex', flexDirection: 'column', gap: 8 }}>
                  <Shimmer height={12} width="30%" />
                  <Shimmer height={14} width="75%" />
                </div>
              ))
            ) : (
              <>
                {/* Address */}
                {(labProfile?.address || labProfile?.city) && (
                  <InfoCard
                    icon={<IconMapPin color={T.teal} />}
                    label="Address"
                    value={[labProfile?.address, labProfile?.city].filter(Boolean).join(', ')}
                    accent={T.teal}
                  />
                )}

                {/* Accreditation */}
                {labProfile?.accreditation && (
                  <InfoCard
                    icon={<IconShield color="#059669" />}
                    label="Accreditation"
                    value={labProfile.accreditation}
                    accent="#059669"
                  />
                )}

                {/* Operating hours */}
                {labProfile?.operatingHours && (
                  <InfoCard
                    icon={<IconClock color={T.navy} />}
                    label="Operating Hours"
                    value={labProfile.operatingHours}
                    accent={T.navy}
                  />
                )}

                {/* Home collection */}
                <InfoCard
                  icon={<IconHome color={T.purple} />}
                  label="Home Collection"
                  value={labProfile?.homeCollectionAvailable ? 'Available — Sample picked up from your location' : 'Not available — Visit lab in person'}
                  accent={T.purple}
                />

                {/* Contact */}
                {(labProfile?.phone || labProfile?.email) && (
                  <InfoCard
                    icon={<IconInfo color={T.textSec} />}
                    label="Contact"
                    value={[labProfile?.phone, labProfile?.email].filter(Boolean).join(' · ')}
                    accent={T.textSec}
                  />
                )}
              </>
            )}
          </div>
        )}
      </div>

      {/* ── Floating Bottom Cart Bar ─────────────────────────────── */}
      {cartCount > 0 && (
        <div
          style={{
            position: 'fixed',
            bottom: 70,
            left: '50%',
            transform: 'translateX(-50%)',
            width: '100%',
            maxWidth: 1080,
            padding: '12px 16px',
            background: T.white,
            borderTop: `1px solid ${T.border}`,
            boxShadow: '0 -4px 20px rgba(0,0,0,0.1)',
            zIndex: 50,
            animation: 'slideUp 0.2s ease',
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 12,
              background: `linear-gradient(135deg, ${T.navy} 0%, #004080 100%)`,
              borderRadius: 14,
              padding: '12px 16px',
            }}
          >
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontFamily: T.font, fontSize: 12, fontWeight: 600, color: 'rgba(255,255,255,0.75)', marginBottom: 1 }}>
                {cartCount} item{cartCount !== 1 ? 's' : ''} · ₹{cartTotal.toLocaleString('en-IN')}
              </div>
              <div style={{ fontFamily: T.font, fontSize: 11, color: 'rgba(255,255,255,0.6)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {cart.map(c => c.name).join(', ')}
              </div>
            </div>
            <button
              onClick={handleBookNow}
              style={{
                background: `linear-gradient(135deg, ${T.teal} 0%, #00A8A8 100%)`,
                border: 'none',
                borderRadius: 10,
                padding: '11px 20px',
                fontFamily: T.font,
                fontSize: 13,
                fontWeight: 800,
                color: T.white,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                boxShadow: `0 2px 10px ${T.teal}50`,
              }}
            >
              Book Now
              <IconArrowRight color={T.white} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Info Card Component ──────────────────────────────────────────────────────

function InfoCard({ icon, label, value, accent }: { icon: React.ReactNode; label: string; value: string; accent: string }) {
  return (
    <div
      style={{
        background: T.white,
        border: `1px solid ${T.border}`,
        borderRadius: 14,
        padding: '14px 16px',
        display: 'flex',
        gap: 12,
        alignItems: 'flex-start',
      }}
    >
      <div
        style={{
          width: 34,
          height: 34,
          borderRadius: 10,
          background: accent + '14',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}
      >
        {icon}
      </div>
      <div>
        <div style={{ fontFamily: T.font, fontSize: 10, fontWeight: 700, color: T.textTer, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4 }}>
          {label}
        </div>
        <div style={{ fontFamily: T.font, fontSize: 13, fontWeight: 500, color: T.text, lineHeight: 1.5 }}>
          {value}
        </div>
      </div>
    </div>
  );
}
